'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function AnnuityCalculator() {
  const t = useTranslations("tools.annuityCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  // Calculator state
  const [inputs, setInputs] = useState({
    startingPrincipal: 20000,
    annualAddition: 10000,
    monthlyAddition: 0,
    timing: 'beginning',
    growthRate: 6,
    years: 10
  });

  const [results, setResults] = useState<any>(null);
  const [schedule, setSchedule] = useState<any[]>([]);

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  useEffect(() => {
    calculateAnnuity();
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  const calculateAnnuity = () => {
    const { startingPrincipal, annualAddition, monthlyAddition, timing, growthRate, years } = inputs;

    const rate = growthRate / 100;
    const isBeginning = timing === 'beginning';

    let balance = startingPrincipal;
    let totalAdditions = 0;
    const scheduleData = [];

    for (let year = 1; year <= years; year++) {
      const startBalance = balance;
      const yearlyAddition = annualAddition + (monthlyAddition * 12);

      if (isBeginning) {
        balance += yearlyAddition;
        balance *= (1 + rate);
      } else {
        balance *= (1 + rate);
        balance += yearlyAddition;
      }

      totalAdditions += yearlyAddition;
      const interestEarned = balance - startBalance - yearlyAddition;

      scheduleData.push({
        year: year,
        startBalance: startBalance,
        additions: yearlyAddition,
        interest: interestEarned,
        endBalance: balance
      });
    }

    const totalInterest = balance - startingPrincipal - totalAdditions;

    setResults({
      endBalance: balance,
      startingPrincipal: startingPrincipal,
      totalAdditions: totalAdditions,
      totalInterest: totalInterest
    });

    setSchedule(scheduleData);
  };

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    calculateAnnuity();
  };

  const handleInputChange = (field: string, value: any) => {
    setInputs(prev => ({ ...prev, [field]: value }));
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  const drawPieChart = () => {
    if (!results) return null;

    const { startingPrincipal, totalAdditions, totalInterest } = results;
    const total = startingPrincipal + totalAdditions + totalInterest;

    const principalPercent = (startingPrincipal / total) * 100;
    const additionsPercent = (totalAdditions / total) * 100;
    const interestPercent = (totalInterest / total) * 100;

    const centerX = 140;
    const centerY = 100;
    const radius = 70;
    let currentAngle = -90;

    const slices = [
      { percent: principalPercent, color: '#2563eb', label: t('chartPrincipal') },
      { percent: additionsPercent, color: '#10b981', label: t('chartAdditions') },
      { percent: interestPercent, color: '#f59e0b', label: t('chartInterest') }
    ];

    return (
      <svg width="280" height="200" style={{ display: 'block', margin: '0 auto' }}>
        {slices.map((slice, index) => {
          const angle = (slice.percent / 100) * 360;
          const endAngle = currentAngle + angle;

          const x1 = centerX + radius * Math.cos(currentAngle * Math.PI / 180);
          const y1 = centerY + radius * Math.sin(currentAngle * Math.PI / 180);
          const x2 = centerX + radius * Math.cos(endAngle * Math.PI / 180);
          const y2 = centerY + radius * Math.sin(endAngle * Math.PI / 180);

          const largeArc = angle > 180 ? 1 : 0;

          const labelAngle = currentAngle + angle / 2;
          const labelRadius = radius * 0.7;
          const labelX = centerX + labelRadius * Math.cos(labelAngle * Math.PI / 180);
          const labelY = centerY + labelRadius * Math.sin(labelAngle * Math.PI / 180);

          const path = `M ${centerX} ${centerY} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2} Z`;

          currentAngle = endAngle;

          return (
            <g key={index}>
              <path d={path} fill={slice.color}>
                <title>{`${slice.label}: ${slice.percent.toFixed(1)}%`}</title>
              </path>
              {slice.percent >= 5 && (
                <text
                  x={labelX}
                  y={labelY}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fill="white"
                  fontWeight="bold"
                  fontSize="14"
                >
                  {slice.percent.toFixed(0)}%
                </text>
              )}
            </g>
          );
        })}
      </svg>
    );
  };

  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    {},
    { marginBottom: '2.5rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6 },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
      borderRadius: '1rem',
      marginBottom: '2rem',
      boxShadow: '0 8px 32px 0 rgba(37, 99, 235, 0.12)'
    },
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
    { padding: '3rem 2rem', borderRadius: '1.5rem' }
  );

  const toolHeaderStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '1.5rem' },
    {},
    { marginBottom: '2rem' },
    { marginBottom: '2.5rem' }
  );

  const h2Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '0.5rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const calculatorLayoutStyle = getResponsiveStyle(
    { display: 'grid', gap: '1.5rem', marginBottom: '2rem' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: '1fr 1fr', gap: '2rem' }
  );

  const calculatorFormStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
    },
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const formGroupStyle = {
    marginBottom: '1.5rem'
  };

  const labelStyle = {
    display: 'block',
    fontWeight: 600,
    color: '#334155',
    marginBottom: '0.5rem',
    fontSize: '0.95rem'
  };

  const inputStyle = getResponsiveStyle(
    {
      width: '100%',
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      fontSize: '1rem',
      transition: 'all 0.3s ease',
      boxSizing: 'border-box' as const
    },
    { padding: '0.75rem' },
    { padding: '0.875rem' },
    { padding: '1rem' }
  );

  const radioGroupStyle = {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.75rem',
    marginTop: '0.5rem'
  };

  const radioOptionStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    cursor: 'pointer'
  };

  const radioInputStyle = {
    width: '1.25rem',
    height: '1.25rem',
    cursor: 'pointer',
    accentColor: '#2563eb'
  };

  const radioLabelStyle = {
    margin: 0,
    cursor: 'pointer',
    fontWeight: 500
  };

  const calculateButtonStyle = getResponsiveStyle(
    {
      width: '100%',
      background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
      color: 'white',
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      marginTop: '1rem'
    },
    { padding: '0.875rem 1.5rem', fontSize: '1rem' },
    { padding: '1rem 2rem', fontSize: '1.125rem' },
    { padding: '1.25rem 2.5rem', fontSize: '1.25rem' }
  );

  const resultsStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
    },
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const resultItemStyle = getResponsiveStyle(
    {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      background: '#f8fafc',
      borderRadius: '0.5rem',
      borderLeft: '4px solid #e2e8f0',
      marginBottom: '0.75rem'
    },
    { padding: '0.75rem' },
    { padding: '0.875rem' },
    { padding: '1rem' }
  );

  const resultItemHighlightStyle = {
    ...resultItemStyle,
    background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
    borderLeftColor: '#2563eb'
  };

  const resultLabelStyle = {
    fontWeight: 600,
    color: '#475569',
    fontSize: '0.95rem'
  };

  const resultValueStyle = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' },
    { fontSize: '1.5rem' }
  );

  const resultValueHighlightStyle = getResponsiveStyle(
    { fontWeight: 700, color: '#2563eb' },
    { fontSize: '1.25rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' }
  );

  const chartContainerStyle = {
    marginTop: '2rem',
    textAlign: 'center' as const
  };

  const chartLegendStyle = getResponsiveStyle(
    { display: 'flex', justifyContent: 'center', flexWrap: 'wrap' as const, marginTop: '1rem' },
    { gap: '1rem', flexDirection: 'column' as const, alignItems: 'flex-start' },
    { gap: '1.5rem', flexDirection: 'row' as const },
    { gap: '1.5rem', flexDirection: 'row' as const }
  );

  const legendItemStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem'
  };

  const legendColorStyle = {
    width: '1rem',
    height: '1rem',
    borderRadius: '0.25rem'
  };

  const legendTextStyle = {
    fontSize: '0.9rem',
    color: '#475569'
  };

  const scheduleTableStyle = getResponsiveStyle(
    {
      width: '100%',
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
      marginTop: '2rem',
      overflowX: 'auto' as const
    },
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const tableStyle = {
    width: '100%',
    borderCollapse: 'collapse' as const
  };

  const thStyle = getResponsiveStyle(
    {
      background: '#f1f5f9',
      textAlign: 'left' as const,
      fontWeight: 600,
      color: '#334155',
      borderBottom: '2px solid #e2e8f0'
    },
    { padding: '0.5rem', fontSize: '0.875rem' },
    { padding: '0.625rem', fontSize: '0.95rem' },
    { padding: '0.75rem', fontSize: '1rem' }
  );

  const tdStyle = getResponsiveStyle(
    { borderBottom: '1px solid #e2e8f0', color: '#475569' },
    { padding: '0.5rem', fontSize: '0.875rem' },
    { padding: '0.625rem', fontSize: '0.95rem' },
    { padding: '0.75rem', fontSize: '1rem' }
  );

  const glassCardStyle = getResponsiveStyle(
    {
      background: 'rgba(255, 255, 255, 0.6)',
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: '1px solid rgba(255, 200, 180, 0.2)',
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
      marginBottom: '2rem'
    },
    { padding: '1.5rem' },
    { padding: '2rem', borderRadius: '1.25rem' },
    { padding: '2.5rem', borderRadius: '1.5rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    { lineHeight: 1.6, color: colors.textSecondary, marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem', lineHeight: 1.7 },
    { fontSize: '1.25rem', lineHeight: 1.8 }
  );

  const listStyle = {
    marginLeft: '1.5rem',
    marginBottom: '1rem'
  };

  const listItemStyle = getResponsiveStyle(
    { marginBottom: '0.75rem', lineHeight: 1.6, color: colors.textSecondary },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const h3Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginTop: '1.5rem', marginBottom: '1rem' },
    { fontSize: '1.25rem' },
    { fontSize: '1.375rem' },
    { fontSize: '1.5rem' }
  );

  const faqItemStyle = {
    marginBottom: '1.5rem'
  };

  const faqQuestionStyle = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '0.5rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' },
    { fontSize: '1.375rem' }
  );

  const referenceStyle = {
    marginBottom: '0.75rem'
  };

  const linkStyle = {
    color: colors.primary,
    textDecoration: 'none',
    fontWeight: 500,
    transition: 'color 0.3s ease'
  };

  return (
    <div style={containerStyle}>
      <header style={headerStyle}>
        <h1 style={h1Style}>{t("title")}</h1>
        <p style={summaryTextStyle}>{t("subtitle")}</p>
      </header>

      <section style={calculatorToolStyle}>
        <div style={toolHeaderStyle}>
          <h2 style={h2Style}>{t("toolTitle")}</h2>
          <p style={summaryTextStyle}>{t("toolSubtitle")}</p>
        </div>

        <div style={calculatorLayoutStyle}>
          <div style={calculatorFormStyle}>
            <form onSubmit={handleCalculate}>
              <div style={formGroupStyle}>
                <label htmlFor="starting-principal" style={labelStyle}>
                  {t("startingPrincipalLabel")}
                </label>
                <input
                  type="number"
                  id="starting-principal"
                  value={inputs.startingPrincipal}
                  onChange={(e) => handleInputChange('startingPrincipal', parseFloat(e.target.value) || 0)}
                  step="0.01"
                  min="0"
                  style={inputStyle}
                  required
                />
              </div>

              <div style={formGroupStyle}>
                <label htmlFor="annual-addition" style={labelStyle}>
                  {t("annualAdditionLabel")}
                </label>
                <input
                  type="number"
                  id="annual-addition"
                  value={inputs.annualAddition}
                  onChange={(e) => handleInputChange('annualAddition', parseFloat(e.target.value) || 0)}
                  step="0.01"
                  min="0"
                  style={inputStyle}
                  required
                />
              </div>

              <div style={formGroupStyle}>
                <label htmlFor="monthly-addition" style={labelStyle}>
                  {t("monthlyAdditionLabel")}
                </label>
                <input
                  type="number"
                  id="monthly-addition"
                  value={inputs.monthlyAddition}
                  onChange={(e) => handleInputChange('monthlyAddition', parseFloat(e.target.value) || 0)}
                  step="0.01"
                  min="0"
                  style={inputStyle}
                  required
                />
              </div>

              <div style={formGroupStyle}>
                <label style={labelStyle}>{t("timingLabel")}</label>
                <div style={radioGroupStyle}>
                  <div style={radioOptionStyle}>
                    <input
                      type="radio"
                      id="beginning"
                      name="timing"
                      value="beginning"
                      checked={inputs.timing === 'beginning'}
                      onChange={(e) => handleInputChange('timing', e.target.value)}
                      style={radioInputStyle}
                    />
                    <label htmlFor="beginning" style={radioLabelStyle}>
                      {t("timingBeginning")}
                    </label>
                  </div>
                  <div style={radioOptionStyle}>
                    <input
                      type="radio"
                      id="end"
                      name="timing"
                      value="end"
                      checked={inputs.timing === 'end'}
                      onChange={(e) => handleInputChange('timing', e.target.value)}
                      style={radioInputStyle}
                    />
                    <label htmlFor="end" style={radioLabelStyle}>
                      {t("timingEnd")}
                    </label>
                  </div>
                </div>
              </div>

              <div style={formGroupStyle}>
                <label htmlFor="growth-rate" style={labelStyle}>
                  {t("growthRateLabel")}
                </label>
                <input
                  type="number"
                  id="growth-rate"
                  value={inputs.growthRate}
                  onChange={(e) => handleInputChange('growthRate', parseFloat(e.target.value) || 0)}
                  step="0.01"
                  min="0"
                  max="100"
                  style={inputStyle}
                  required
                />
              </div>

              <div style={formGroupStyle}>
                <label htmlFor="years" style={labelStyle}>
                  {t("yearsLabel")}
                </label>
                <input
                  type="number"
                  id="years"
                  value={inputs.years}
                  onChange={(e) => handleInputChange('years', parseInt(e.target.value) || 0)}
                  step="1"
                  min="1"
                  max="100"
                  style={inputStyle}
                  required
                />
              </div>

              <button type="submit" style={calculateButtonStyle}>
                {t("calculateButton")}
              </button>
            </form>
          </div>

          {results && (
            <div style={resultsStyle}>
              <h3 style={h2Style}>{t("resultsTitle")}</h3>
              <div style={resultItemHighlightStyle}>
                <span style={resultLabelStyle}>{t("endBalanceLabel")}</span>
                <span style={resultValueHighlightStyle}>{formatCurrency(results.endBalance)}</span>
              </div>
              <div style={resultItemStyle}>
                <span style={resultLabelStyle}>{t("startingPrincipalResultLabel")}</span>
                <span style={resultValueStyle}>{formatCurrency(results.startingPrincipal)}</span>
              </div>
              <div style={resultItemStyle}>
                <span style={resultLabelStyle}>{t("totalAdditionsLabel")}</span>
                <span style={resultValueStyle}>{formatCurrency(results.totalAdditions)}</span>
              </div>
              <div style={resultItemStyle}>
                <span style={resultLabelStyle}>{t("totalInterestLabel")}</span>
                <span style={resultValueStyle}>{formatCurrency(results.totalInterest)}</span>
              </div>

              <div style={chartContainerStyle}>
                {drawPieChart()}
                <div style={chartLegendStyle}>
                  <div style={legendItemStyle}>
                    <div style={{...legendColorStyle, background: '#2563eb'}}></div>
                    <span style={legendTextStyle}>{t("chartPrincipal")}</span>
                  </div>
                  <div style={legendItemStyle}>
                    <div style={{...legendColorStyle, background: '#10b981'}}></div>
                    <span style={legendTextStyle}>{t("chartAdditions")}</span>
                  </div>
                  <div style={legendItemStyle}>
                    <div style={{...legendColorStyle, background: '#f59e0b'}}></div>
                    <span style={legendTextStyle}>{t("chartInterest")}</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {schedule.length > 0 && (
          <div style={scheduleTableStyle}>
            <h3 style={h2Style}>{t("scheduleTitle")}</h3>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>{t("scheduleYear")}</th>
                  <th style={thStyle}>{t("scheduleStartBalance")}</th>
                  <th style={thStyle}>{t("scheduleAdditions")}</th>
                  <th style={thStyle}>{t("scheduleInterest")}</th>
                  <th style={thStyle}>{t("scheduleEndBalance")}</th>
                </tr>
              </thead>
              <tbody>
                {schedule.map((row) => (
                  <tr key={row.year}>
                    <td style={tdStyle}>{row.year}</td>
                    <td style={tdStyle}>{formatCurrency(row.startBalance)}</td>
                    <td style={tdStyle}>{formatCurrency(row.additions)}</td>
                    <td style={tdStyle}>{formatCurrency(row.interest)}</td>
                    <td style={{...tdStyle, fontWeight: 600}}>{formatCurrency(row.endBalance)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("introTitle")}</h2>
        <p style={paragraphStyle}>{t("introParagraph1")}</p>
        <p style={paragraphStyle}>{t("introParagraph2")}</p>
        <p style={paragraphStyle}>{t("introParagraph3")}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("howToUseTitle")}</h2>
        <ol style={listStyle}>
          <li style={listItemStyle}>{t("howToUseStep1")}</li>
          <li style={listItemStyle}>{t("howToUseStep2")}</li>
          <li style={listItemStyle}>{t("howToUseStep3")}</li>
          <li style={listItemStyle}>{t("howToUseStep4")}</li>
          <li style={listItemStyle}>{t("howToUseStep5")}</li>
          <li style={listItemStyle}>{t("howToUseStep6")}</li>
          <li style={listItemStyle}>{t("howToUseStep7")}</li>
        </ol>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("insightsTitle")}</h2>

        <h3 style={h3Style}>{t("insightsRetirementTitle")}</h3>
        <p style={paragraphStyle}>{t("insightsRetirementText")}</p>

        <h3 style={h3Style}>{t("insightsComparisonTitle")}</h3>
        <p style={paragraphStyle}>{t("insightsComparisonText")}</p>

        <h3 style={h3Style}>{t("insightsTaxTitle")}</h3>
        <p style={paragraphStyle}>{t("insightsTaxText")}</p>

        <h3 style={h3Style}>{t("insightsVariabilityTitle")}</h3>
        <p style={paragraphStyle}>{t("insightsVariabilityText")}</p>

        <h3 style={h3Style}>{t("insightsProtectionTitle")}</h3>
        <p style={paragraphStyle}>{t("insightsProtectionText")}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("bestPracticesTitle")}</h2>

        <h3 style={h3Style}>{t("bestPracticesClarifyTitle")}</h3>
        <p style={paragraphStyle}>{t("bestPracticesClarifyText")}</p>

        <h3 style={h3Style}>{t("bestPracticesTimingTitle")}</h3>
        <p style={paragraphStyle}>{t("bestPracticesTimingText")}</p>

        <h3 style={h3Style}>{t("bestPracticesReturnsTitle")}</h3>
        <p style={paragraphStyle}>{t("bestPracticesReturnsText")}</p>

        <h3 style={h3Style}>{t("bestPracticesFeesTitle")}</h3>
        <p style={paragraphStyle}>{t("bestPracticesFeesText")}</p>

        <h3 style={h3Style}>{t("bestPracticesInflationTitle")}</h3>
        <p style={paragraphStyle}>{t("bestPracticesInflationText")}</p>

        <h3 style={h3Style}>{t("bestPracticesLongevityTitle")}</h3>
        <p style={paragraphStyle}>{t("bestPracticesLongevityText")}</p>

        <h3 style={h3Style}>{t("bestPracticesHolisticTitle")}</h3>
        <p style={paragraphStyle}>{t("bestPracticesHolisticText")}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("faqTitle")}</h2>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t("faq1Question")}</h3>
          <p style={paragraphStyle}>{t("faq1Answer")}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t("faq2Question")}</h3>
          <p style={paragraphStyle}>{t("faq2Answer")}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t("faq3Question")}</h3>
          <p style={paragraphStyle}>{t("faq3Answer")}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t("faq4Question")}</h3>
          <p style={paragraphStyle}>{t("faq4Answer")}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t("faq5Question")}</h3>
          <p style={paragraphStyle}>{t("faq5Answer")}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t("faq6Question")}</h3>
          <p style={paragraphStyle}>{t("faq6Answer")}</p>
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("referencesTitle")}</h2>
        <div style={referenceStyle}>
          <a href="https://www.calculator.net/annuity-calculator.html" target="_blank" rel="noopener noreferrer" style={linkStyle}>
            {t("reference1")}
          </a>
        </div>
        <div style={referenceStyle}>
          <a href="https://www.bankrate.com/investing/annuity-calculator/" target="_blank" rel="noopener noreferrer" style={linkStyle}>
            {t("reference2")}
          </a>
        </div>
        <div style={referenceStyle}>
          <a href="https://www.tiaa.org/public/tools-calculators/annuity-payout" target="_blank" rel="noopener noreferrer" style={linkStyle}>
            {t("reference3")}
          </a>
        </div>
        <div style={referenceStyle}>
          <a href="https://oci.wi.gov/Documents/Consumers/PI-214.pdf" target="_blank" rel="noopener noreferrer" style={linkStyle}>
            {t("reference4")}
          </a>
        </div>
        <div style={referenceStyle}>
          <a href="https://www.annuity.org/annuities/" target="_blank" rel="noopener noreferrer" style={linkStyle}>
            {t("reference5")}
          </a>
        </div>
        <div style={referenceStyle}>
          <a href="https://www.ncoa.org/article/what-is-an-annuity-and-how-does-it-work/" target="_blank" rel="noopener noreferrer" style={linkStyle}>
            {t("reference6")}
          </a>
        </div>
        <div style={referenceStyle}>
          <a href="https://www.guardianlife.com/annuities" target="_blank" rel="noopener noreferrer" style={linkStyle}>
            {t("reference7")}
          </a>
        </div>
      </section>
    </div>
  );
}